import React, { useState } from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { ChargingSessionModelV1 } from 'src/Libs/e-mobility/charging-sessions'
import { ChargingSessionCsvDataModel, ChargingSessionCsvHeadersModel } from 'src/Libs/e-mobility/charging-sessions/models/charging-session.model'
import { generateChargingSessionsCsv } from '../utils/generate-charging-session-csv'
import { CSVLink } from 'react-csv'
import { Menu, MenuItem } from '@mui/material'
import { makeStyles } from 'tss-react/mui'

const useStyles = makeStyles()(theme => ({
  menu: {
    backgroundColor: theme.palette.background.paper,
    marginTop: theme.spacing(2),
  },
  menuItem: {
    minWidth: 150,
    height: theme.shape.filter.menuItemHeight,
    '&.MuiMenuItem-root:hover': {
      backgroundColor: theme.palette.filter.menuItemHoverBackground,
    },
  },
  csvLink: {
    width: '100%',
    height: '100%',
    textDecoration: 'none',
    color: theme.palette.grey.grey00,
  },
}))

interface ExportChargingSessionsMenuProps {
  isOpen: boolean
  anchorEl: null | HTMLElement
  handleClose: () => void
  sessions: ChargingSessionModelV1[]
}

interface CsvDataToExport {
  headers: ChargingSessionCsvHeadersModel[]
  data: ChargingSessionCsvDataModel[]
}

const SEPARATOR = ';'
const FILE_NAME = 'E3DC.csv'

/**
 * Menu component with export options (PDF, CSV...).
 * @param isOpen boolean which controls if menu is open or closed
 * @param anchorEl anchor of menu element
 * @param handleClose function for closing menu
 * @param sessions array of ChargingSessionModel
 * @return JSX Element
 */
const ExportChargingSessionsMenu: React.VFC<ExportChargingSessionsMenuProps> = ({ isOpen, anchorEl, handleClose, sessions }) => {
  const [csvDataToExport, setCsvDataToExport] = useState<CsvDataToExport>({ headers: [], data: [] })

  const { t } = useCustomTranslation()
  const { classes } = useStyles()

  const handleExportCsv = (): void => {
    const { csvData, csvHeaders } = generateChargingSessionsCsv(sessions, t)
    setCsvDataToExport({ headers: csvHeaders, data: csvData })
    handleClose()
  }

  return (
    <Menu PaperProps={{ className: classes.menu }} anchorEl={anchorEl} open={isOpen} onClose={handleClose}>
      <MenuItem className={classes.menuItem}>
        <CSVLink
          data={csvDataToExport.data}
          headers={csvDataToExport.headers}
          separator={SEPARATOR}
          filename={FILE_NAME}
          onClick={handleExportCsv}
          className={classes.csvLink}
        >
          {t('emobility.chargingSession.csv-export')}
        </CSVLink>
      </MenuItem>
    </Menu>
  )
}

export default ExportChargingSessionsMenu
